import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLangPath } from '../../hooks/useLangPath';

/* ── Inline SVG illustration: engineering workflow diagram ── */
const HowWeWorkIllustration = () => (
  <svg
    viewBox="0 0 480 420"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: '100%', maxWidth: 480, height: 'auto', display: 'block', margin: '0 auto' }}
    aria-hidden="true"
  >
    <defs>
      <radialGradient id="bgGrad" cx="50%" cy="50%" r="60%">
        <stop offset="0%" stopColor="#12122a" />
        <stop offset="100%" stopColor="#05050f" />
      </radialGradient>
      <radialGradient id="glowPurple" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#8258c8" stopOpacity="0.35" />
        <stop offset="100%" stopColor="#8258c8" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="glowTeal" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#45d0bd" stopOpacity="0.35" />
        <stop offset="100%" stopColor="#45d0bd" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="glowBlue" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#2c84c8" stopOpacity="0.35" />
        <stop offset="100%" stopColor="#2c84c8" stopOpacity="0" />
      </radialGradient>
      <filter id="glow4">
        <feGaussianBlur stdDeviation="4" result="blur" />
        <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
      <filter id="glow8">
        <feGaussianBlur stdDeviation="8" result="blur" />
        <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
      <linearGradient id="lineGrad1" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#8258c8" stopOpacity="0.6" />
        <stop offset="100%" stopColor="#45d0bd" stopOpacity="0.6" />
      </linearGradient>
      <linearGradient id="lineGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#45d0bd" stopOpacity="0.6" />
        <stop offset="100%" stopColor="#2c84c8" stopOpacity="0.6" />
      </linearGradient>
      <linearGradient id="centerGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#8258c8" />
        <stop offset="100%" stopColor="#2c84c8" />
      </linearGradient>
    </defs>

    {/* Background */}
    <rect width="480" height="420" rx="20" fill="url(#bgGrad)" />

    {/* Grid lines subtle */}
    {[60,120,180,240,300,360].map(x => (
      <line key={`vg${x}`} x1={x} y1="20" x2={x} y2="400" stroke="rgba(255,255,255,0.03)" strokeWidth="1"/>
    ))}
    {[60,120,180,240,300,360].map(y => (
      <line key={`hg${y}`} x1="20" y1={y} x2="460" y2={y} stroke="rgba(255,255,255,0.03)" strokeWidth="1"/>
    ))}

    {/* Central hub glow */}
    <ellipse cx="240" cy="210" rx="120" ry="100" fill="url(#glowPurple)" />

    {/* Connection lines between nodes */}
    {/* Discover → Architect */}
    <line x1="100" y1="105" x2="175" y2="155" stroke="url(#lineGrad1)" strokeWidth="1.5" strokeDasharray="6 3" />
    {/* Architect → Engineer (center) */}
    <line x1="185" y1="165" x2="225" y2="205" stroke="#8258c8" strokeWidth="1.5" strokeOpacity="0.5" strokeDasharray="6 3" />
    {/* Center → Deploy */}
    <line x1="255" y1="205" x2="295" y2="165" stroke="#45d0bd" strokeWidth="1.5" strokeOpacity="0.5" strokeDasharray="6 3" />
    {/* Deploy → Optimize */}
    <line x1="305" y1="155" x2="380" y2="105" stroke="url(#lineGrad2)" strokeWidth="1.5" strokeDasharray="6 3" />
    {/* Center → bottom */}
    <line x1="240" y1="255" x2="240" y2="310" stroke="#8258c8" strokeWidth="1.5" strokeOpacity="0.4" strokeDasharray="6 3" />

    {/* Outer circuit traces */}
    <path d="M 60 330 L 60 360 L 160 360" stroke="rgba(130,88,200,0.25)" strokeWidth="1" fill="none" />
    <path d="M 420 330 L 420 360 L 320 360" stroke="rgba(69,208,189,0.25)" strokeWidth="1" fill="none" />
    <path d="M 60 90 L 60 60 L 160 60" stroke="rgba(44,132,200,0.2)" strokeWidth="1" fill="none" />
    <path d="M 420 90 L 420 60 L 320 60" stroke="rgba(130,88,200,0.2)" strokeWidth="1" fill="none" />

    {/* Corner dots on circuit traces */}
    <circle cx="60" cy="360" r="3" fill="rgba(130,88,200,0.4)" />
    <circle cx="420" cy="360" r="3" fill="rgba(69,208,189,0.4)" />
    <circle cx="60" cy="60" r="3" fill="rgba(44,132,200,0.3)" />
    <circle cx="420" cy="60" r="3" fill="rgba(130,88,200,0.3)" />

    {/* ── Step 1: Discover (top-left) ── */}
    <ellipse cx="100" cy="100" rx="55" ry="40" fill="rgba(130,88,200,0.08)" />
    <circle cx="100" cy="100" r="34" fill="rgba(130,88,200,0.12)" stroke="#8258c8" strokeWidth="1.5" strokeOpacity="0.6" filter="url(#glow4)" />
    <circle cx="100" cy="100" r="24" fill="rgba(130,88,200,0.18)" />
    {/* Search icon */}
    <circle cx="97" cy="97" r="9" fill="none" stroke="#8258c8" strokeWidth="2" filter="url(#glow4)" />
    <line x1="104" y1="104" x2="110" y2="110" stroke="#8258c8" strokeWidth="2.5" strokeLinecap="round" filter="url(#glow4)" />
    <text x="100" y="142" textAnchor="middle" fill="#8258c8" fontSize="10" fontWeight="700" fontFamily="DM Sans, sans-serif" letterSpacing="1">DISCOVER</text>

    {/* ── Step 2: Architect (mid-left) ── */}
    <ellipse cx="180" cy="170" rx="45" ry="35" fill="rgba(130,88,200,0.06)" />
    <circle cx="180" cy="170" r="28" fill="rgba(130,88,200,0.10)" stroke="#8258c8" strokeWidth="1.2" strokeOpacity="0.5" filter="url(#glow4)" />
    <circle cx="180" cy="170" r="18" fill="rgba(130,88,200,0.14)" />
    {/* Blueprint icon */}
    <rect x="172" y="163" width="16" height="14" rx="2" fill="none" stroke="#8258c8" strokeWidth="1.5" filter="url(#glow4)" />
    <line x1="175" y1="167" x2="185" y2="167" stroke="#8258c8" strokeWidth="1" />
    <line x1="175" y1="170" x2="185" y2="170" stroke="#8258c8" strokeWidth="1" />
    <line x1="175" y1="173" x2="182" y2="173" stroke="#8258c8" strokeWidth="1" />
    <text x="180" y="206" textAnchor="middle" fill="#8258c8" fontSize="9" fontWeight="700" fontFamily="DM Sans, sans-serif" letterSpacing="0.8">ARCHITECT</text>

    {/* ── Center hub: Engineer ── */}
    <ellipse cx="240" cy="210" rx="80" ry="65" fill="url(#glowPurple)" />
    <circle cx="240" cy="210" r="46" fill="rgba(130,88,200,0.06)" stroke="rgba(130,88,200,0.15)" strokeWidth="1" />
    <circle cx="240" cy="210" r="36" fill="rgba(10,10,24,0.7)" stroke="url(#centerGrad)" strokeWidth="2" filter="url(#glow8)" />
    <circle cx="240" cy="210" r="26" fill="rgba(130,88,200,0.12)" />
    {/* Gear/code icon */}
    <text x="240" y="217" textAnchor="middle" fill="#fff" fontSize="22" fontFamily="sans-serif">⚙️</text>
    <text x="240" y="256" textAnchor="middle" fill="rgba(255,255,255,0.9)" fontSize="10" fontWeight="800" fontFamily="DM Sans, sans-serif" letterSpacing="1.5">ENGINEER</text>

    {/* ── Step 4: Deploy (mid-right) ── */}
    <ellipse cx="300" cy="170" rx="45" ry="35" fill="rgba(69,208,189,0.06)" />
    <circle cx="300" cy="170" r="28" fill="rgba(69,208,189,0.10)" stroke="#45d0bd" strokeWidth="1.2" strokeOpacity="0.5" filter="url(#glow4)" />
    <circle cx="300" cy="170" r="18" fill="rgba(69,208,189,0.14)" />
    {/* Rocket icon */}
    <path d="M 300 160 C 298 163 295 168 296 173 L 304 173 C 305 168 302 163 300 160Z" fill="#45d0bd" opacity="0.8" filter="url(#glow4)" />
    <circle cx="300" cy="175" r="2" fill="#45d0bd" />
    <text x="300" y="206" textAnchor="middle" fill="#45d0bd" fontSize="9" fontWeight="700" fontFamily="DM Sans, sans-serif" letterSpacing="0.8">DEPLOY</text>

    {/* ── Step 5: Optimize (top-right) ── */}
    <ellipse cx="380" cy="100" rx="55" ry="40" fill="rgba(44,132,200,0.08)" />
    <circle cx="380" cy="100" r="34" fill="rgba(44,132,200,0.12)" stroke="#2c84c8" strokeWidth="1.5" strokeOpacity="0.6" filter="url(#glow4)" />
    <circle cx="380" cy="100" r="24" fill="rgba(44,132,200,0.18)" />
    {/* Chart/optimize icon */}
    <polyline points="368,107 373,101 378,104 384,96 389,99 393,93" fill="none" stroke="#2c84c8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#glow4)" />
    <text x="380" y="142" textAnchor="middle" fill="#2c84c8" fontSize="10" fontWeight="700" fontFamily="DM Sans, sans-serif" letterSpacing="1">OPTIMIZE</text>

    {/* ── Bottom panel: stats ── */}
    <rect x="40" y="310" width="120" height="70" rx="12" fill="rgba(130,88,200,0.08)" stroke="rgba(130,88,200,0.2)" strokeWidth="1" />
    <text x="100" y="335" textAnchor="middle" fill="#8258c8" fontSize="20" fontWeight="900" fontFamily="DM Sans, sans-serif">98%</text>
    <text x="100" y="352" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9" fontFamily="sans-serif">Client Satisfaction</text>
    <rect x="42" y="358" width="116" height="1" fill="rgba(130,88,200,0.2)" />
    <circle cx="56" cy="370" r="3" fill="#8258c8" opacity="0.6" />
    <rect x="63" y="368" width="70" height="4" rx="2" fill="rgba(130,88,200,0.25)" />
    <rect x="63" y="368" width="58" height="4" rx="2" fill="rgba(130,88,200,0.5)" />

    <rect x="180" y="310" width="120" height="70" rx="12" fill="rgba(69,208,189,0.08)" stroke="rgba(69,208,189,0.2)" strokeWidth="1" />
    <text x="240" y="335" textAnchor="middle" fill="#45d0bd" fontSize="20" fontWeight="900" fontFamily="DM Sans, sans-serif">10+</text>
    <text x="240" y="352" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9" fontFamily="sans-serif">Industries Served</text>
    <rect x="182" y="358" width="116" height="1" fill="rgba(69,208,189,0.2)" />
    <circle cx="196" cy="370" r="3" fill="#45d0bd" opacity="0.6" />
    <rect x="203" y="368" width="70" height="4" rx="2" fill="rgba(69,208,189,0.25)" />
    <rect x="203" y="368" width="62" height="4" rx="2" fill="rgba(69,208,189,0.5)" />

    <rect x="320" y="310" width="120" height="70" rx="12" fill="rgba(44,132,200,0.08)" stroke="rgba(44,132,200,0.2)" strokeWidth="1" />
    <text x="380" y="335" textAnchor="middle" fill="#2c84c8" fontSize="20" fontWeight="900" fontFamily="DM Sans, sans-serif">7+</text>
    <text x="380" y="352" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9" fontFamily="sans-serif">Years Engineering</text>
    <rect x="322" y="358" width="116" height="1" fill="rgba(44,132,200,0.2)" />
    <circle cx="336" cy="370" r="3" fill="#2c84c8" opacity="0.6" />
    <rect x="343" y="368" width="70" height="4" rx="2" fill="rgba(44,132,200,0.25)" />
    <rect x="343" y="368" width="55" height="4" rx="2" fill="rgba(44,132,200,0.5)" />

    {/* Floating particles */}
    <circle cx="150" cy="240" r="2" fill="#8258c8" opacity="0.5" />
    <circle cx="340" cy="240" r="2" fill="#45d0bd" opacity="0.5" />
    <circle cx="60" cy="200" r="1.5" fill="#2c84c8" opacity="0.4" />
    <circle cx="420" cy="200" r="1.5" fill="#8258c8" opacity="0.4" />
    <circle cx="200" cy="70" r="2" fill="#45d0bd" opacity="0.4" />
    <circle cx="290" cy="70" r="2" fill="#2c84c8" opacity="0.4" />
    <circle cx="140" cy="290" r="1.5" fill="#8258c8" opacity="0.35" />
    <circle cx="350" cy="290" r="1.5" fill="#45d0bd" opacity="0.35" />
  </svg>
);
 
const Faq = () => {
    const { t } = useTranslation();
    const getPath = useLangPath();
    const [activeId, setActiveId] = useState('step1');

    const toggleAccordion = (id) => {
        setActiveId(activeId === id ? null : id);
    };

    const stepsData = [
        {
            id: 'step1',
            title: t('01. Discover'),
            content: t('We begin with a deep dive into your business needs and technical challenges to identify ideal opportunities for innovation.')
        },
        {
            id: 'step2',
            title: t('02. Architect'),
            content: t('We design the systems technical blueprint, focusing on scalability and the seamless connection between software and hardware.')
        },
        {
            id: 'step3',
            title: t('03. Engineer'),
            content: t('The actual building phase using cutting-edge AI, IoT, and robotics technologies with the highest engineering standards.')
        },
        {
            id: 'step4',
            title: t('04. Deploy'),
            content: t('Launching the system into the real-world environment while ensuring smooth integration with your existing operations.')
        },
        {
            id: 'step5',
            title: t('05. Optimize'),
            content: t('Continuous monitoring and data-driven improvements to ensure your system maintains peak performance and sustainability.')
        }
    ];

    return (
        <section className="faq-sec3 ibt-section-gapBottom py-5">
            <div className="container">
                <div className="row align-items-center">
                    
                    <div className="col-lg-6 col-12 mb-5 mb-lg-0">
                        <div className="faq-content pe-lg-4">
                            <div className="sec-title white mb-4">
                                <h2 className="title animated-heading h1 fw-bold text-white">
                                    {t("How We Work")}
                                </h2>
                            </div>

                            <div className="accordion custom-accordion" id="workProcessAccordion">
                                {stepsData.map((item) => (
                                    <div className="accordion-item bg-transparent border-0 mb-3" key={item.id}>
                                        <h2 className="accordion-header">
                                            <button 
                                                className={`accordion-button shadow-none ${activeId === item.id ? '' : 'collapsed'}`}
                                                type="button"
                                                onClick={() => toggleAccordion(item.id)}
                                            >
                                                {item.title}
                                            </button>
                                        </h2>
                                        <div className={`accordion-collapse collapse ${activeId === item.id ? 'show' : ''}`}>
                                            <div className="accordion-body text-light opacity-75">
                                                {item.content}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div style={{ marginTop: '28px' }}>
                                 <Link to={getPath('/services')} className="btn-capabilities">
                                    <span>{t("View Our Capabilities")}</span>
                                    <i className="icon-arrow-top" style={{ marginLeft: '12px' }}></i>
                                 </Link>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-6 col-12 text-center">
                        <div className="faq-img-wrapper position-relative" style={{ borderRadius: 20, overflow: 'hidden' }}>
                            <HowWeWorkIllustration />
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default Faq;